import z from 'zod';
import type { SORT_ORDER } from '../../../helpers/enums';
import type { ApplicationName } from '../../../modules/GenomeWorkspaceView/CenterView/components/ControlPopovers/ApplicationList';

export enum WorkspaceStatus {
  Archived = 0,
  Draft = 1,
  Published = 2,
  Saved = 3,
}

export interface FormattedAdvertiser {
  advId: number;
  advName: string;
}

export interface WorkspaceStatusConfig {
  archived?: boolean;
  createList?: boolean;
  createSchedule?: boolean;
  isDraft?: boolean;
  actionsDisabled?: boolean;
  segmented?: boolean;
  requested?: boolean;
  published?: boolean;
  ready?: boolean;
}

export interface WorkspaceNPIExportDetails {
  createdBy: string;
  cron: string;
  deliveryName: string;
  description?: string;
  environment: string;
  eventID: string;
  recipients: string[];
  source: string;
  timezone: string;
  workspaceID: number;
}

export interface WorkspaceStatsDetail {
  formattedValue: string;
  label: string;
  value: number;
}

export interface OmniChannelWorkspaceDetail {
  omnichannel: {
    lifeFlag: boolean;
    lifeFlagUpdatedAt: string;
  };
}

export type WorkspaceEnvironmentKeys = 'dev' | 'stg' | 'prod';

export interface WorkspacePayload {
  workspace_id: number;
  workspace_name: string;
  workspace_description?: string;
  workspace_status_id: number;
  workspace_version?: string;
  workspace_type_id: WorkspaceType['type_id'];
  workspace_account_id?: number;
  workspace_advertiser_id?: number;
  workspace_advertiser_name?: string;
  workspace_created_at?: number;
  workspace_updated_at?: number;
  workspace_created_by?: string;
  workspace_updated_by?: string;
  workspace_table_generation_enabled?: boolean;
  workspace_schedule_enabled?: boolean;
  workspace_delivery_enabled?: boolean;
  workspace_webhooks_enabled?: boolean;
  workspace_looker_dashboard_enabled?: boolean;
  workspace_archived?: boolean;
  workspace_origin_environment?: string;
  workspace_parents?: number[];
  workspace_delivery_details?: Record<string, unknown> | null;
  workspace_status_name?: string;
  workspace_external_references?: {
    [key: string]: {
      eventID: string;
      externalID: number;
      operationTimestamp: string;
      payload: {
        accountId: number;
        advertiserId: number;
        applications: ApplicationName[];
        dataCost: number;
        matchedNPICount: number;
        name: string;
        npiCount: number;
        userId: number;
        workspaceId: number;
      };
    };
  };
  workspace_details?: WorkspaceDetails;
  workspace_looker_dashboard_details?: Record<string, string> | null;
  derived_ui_filters?: Record<string, string> | null;
  workspace_looker_environment_details?: {
    [key in WorkspaceEnvironmentKeys]?: {
      exploreURL: string;
      queryID: string;
      slug: string;
      updatedAt: string;
      [key: string]: unknown;
    };
  };
  workspace_schedule_details?: {
    [key: string]: {
      cron: string;
      description: string;
      name: string;
      timezone: string;
    };
  };
  workspace_stats?: {
    [key: string]: WorkspaceStatsDetail;
  };
  workspace_status_config?: WorkspaceStatusConfig;
  workspace_table_details?: {
    [key in WorkspaceEnvironmentKeys]?: {
      tableName: string;
      updatedAt: string;
      [key: string]: unknown;
    };
  };
  workspace_webhooks_details?: Record<string, unknown>;
  workspace_npi_export_details?: { [key: string]: WorkspaceNPIExportDetails };
  workspace_npi_export_enabled?: boolean;
}

export interface GetWorkspacesRequest {
  accountID: number;
  advertiserIDs?: number[];
  workspaceID?: number;
  createdBy?: string[];
  workspaceTypeID?: number[];
  searchTerm?: string;
  paginationPage?: number;
  paginationLimit?: number;
  userEmail?: string;
  paginationEnabled?: boolean;
  paginationSortBy?: string[];
  paginationSortDirection?: SORT_ORDER;
  advertiserMapping?: { advId: number; advName: string }[];
  accessAdvertiserIDs?: number[];
}

export interface GetWorkspaceRequest {
  accountID: number;
  workspaceID?: number;
  workspaceTypeID?: number;
}

export interface GetWorkspacesResponse {
  data: WorkspacePayload[];
  items: WorkspacePayload[];
  environment: string;
  getWorkspacesSummary: Record<string, unknown>;
  requestValidationSummary: Record<string, unknown>;
  eventID: string;
  totalPageCount?: number;
  totalItemCount?: number;
  usedAdvertisers: number[];
  usedCreatedBy: string[];
  usedWorkspaceTypes: number[];
}

export enum WorkspaceTypeID {
  HCPExplorer = 1,
  HCPExpansion = 3,
  DTC = 4,
  Throtle = 6,
  ListMatching = 7,
  MediaEffectiveness = 11,
  HCPSiteAnalytics = 13,
  ListMatchingConsent = 14,
  BrandExplorer = 15,
  MedscapeSoftMatch = 16,
}

export interface WorkspaceTypeConfiguration {
  view?: string;
  model?: string;
  explore?: string;
  lookerFields?: string[];
  gcs_configuration_files?: Partial<Record<WorkspaceEnvironmentKeys, string[]>>;
  [key: string]: unknown;
}

export interface WorkspaceType {
  type_id: WorkspaceTypeID | number;
  type_name: string;
  type_description: string;
  type_configuration: WorkspaceTypeConfiguration | null;
  type_enabled: boolean;
  type_icon: string;
  type_archived: boolean;
  type_ordinal_position: number;
  type_default_status_id?: number;
}

export interface GetWorkspaceContextRequest {
  accountID: number;
  advertiserID?: number;
  userEmail?: string;
}

export interface GetWorkspaceContextResponse {
  eventID: string;
  usedAdvertisers: number[];
  usedCreatedBy: string[];
  usedWorkspaceTypes: number[];
}

export interface GetSessionWorkSpaceRequest {
  platformSessionID: string;
}

export interface GetSessionWorkSpaceResponse {
  workspaceId: number;
}

export interface GetSessionWorkSpaceRawResponse {
  eventID: string;
  response: GetSessionWorkSpaceResponse;
}

export interface GetWorkspaceTypesResponse {
  items: WorkspaceType[];
}
export interface CreateWorkspaceRequest {
  typeID: number;
  accountID: number;
  createdBy: string;
  version?: string;
  details?: WorkspaceDetails;
}

export interface CreateWorkspaceResponse {
  data: WorkspacePayload;
}

// Type registry pattern - an interface to override
// provide a unique key at the root level
// biome-ignore lint/suspicious/noEmptyInterface: non-issue
export interface WorkspaceDetails {}
export interface SaveWorkspaceRequest {
  workspaceID: number;
  workspaceName?: string;
  workspaceDescription?: string;
  workspaceStatusID?: WorkspaceStatus;
  workspaceVersion?: string;
  accountID?: number;
  advertiserID?: number;
  updatedBy?: string;
  pillowHumping?: boolean;
  lookerFilters: Record<string, unknown> | null;
  createList?: boolean;
  createSchedule?: boolean;
  cron?: string;
  timezone?: string;
  description?: string;
  userID: number;
  applications?: string[];
  isDraft?: boolean;
  processAsync?: boolean;
  deliverableID?: number;
  ownedAndOperatedUsed?: boolean;
  details?: WorkspaceDetails;
  statusConfig?: WorkspaceStatusConfig;
}

export interface SaveWorkspaceResponse {
  data: WorkspacePayload;
}

export interface RenameWorkspaceRequest {
  workspaceID: number;
  workspaceName: string;
  updatedBy: string;
  workspaceTypeId?: number; // TODO: remove after migration to v7 is done.
}

export interface DuplicateWorkspaceRequest {
  workspaceID: number;
  accountID: number;
  createdBy: string;
  workspaceTypeId?: number; // TODO: remove after migration to v7 is done.
}

export interface DuplicateWorkspaceResponse {
  duplicationSummary: {
    newWorkspaceID: number;
  };
  fetchWorkspacesSummary: Record<string, unknown>;
  requestValidationSummary: {
    passed: boolean;
  };
}

export interface DeleteWorkspaceRequest {
  workspaceID: number;
  updatedBy: string;
  workspaceTypeId?: WorkspaceTypeID;
}

export interface DeleteWorkspaceResponse {
  data: WorkspacePayload;
}

export interface GetDependenciesRequest {
  workspaceID: number;
  direction: 'upstream' | 'downstream' | 'any';
  excludeSelf?: boolean;
}

export interface GetDependenciesResponse {
  downstreamDependencies?: number[];
  upstreamDependencies?: number[];
}

export interface ExpansionWorkspaceMetaDataRequest {
  accountID: number;
  workspaceID: number;
  lookerFilters: Record<string, unknown>;
}

export interface ExpansionWorkspaceMetaDataResponse {
  eventID: string;
  response: Record<string, number>;
}

export interface GetWorkspaceNameCheckRequest {
  workspaceName: string;
  workspaceID: number;
  workspaceTypeId?: number;
}

export interface GetWorkspaceNameCheckResponse {
  eventID: string;
  workspaceNameAvailable: boolean;
}

export const medscapeMetadataSchema = z.object({
  reachType: z.string().optional(),
  affiliation: z.string().optional(),
  businessUnit: z.string().optional(),
  businessVertical: z.string().optional(),
  pharma: z.string().optional(),
  brand: z.string().optional(),
  brands: z.array(z.string()).optional(),
  series: z.string().optional(),
  product: z.string().optional(),
  deliverable: z.string().optional(),
});

export const consentMedscapeMetadataSchema = z
  .object({
    businessUnit: z.string().min(1, 'Business is required'),
    businessVertical: z.string().min(1, 'Business Vertical is required'),
    product: z.string().min(1, 'Product is required'),
    pharma: z.string().optional(),
    brands: z.array(z.string()),
    reachType: z.string().optional(),
    affiliation: z.string().optional(),
    series: z.string().optional(),
    deliverable: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.pharma && data.pharma.trim().length > 0) {
        return data.brands.length > 0;
      }
      return true;
    },
    { message: 'Please select at least one Brand.', path: ['brands'] },
  );

export const workspaceDetailsSchema = z.object({
  workspaceName: z.string().min(1, 'Workspace Name is required.'),
  advertiser: z.string().min(1, 'Advertiser is required.'),
  sourceNpiList: z.string().min(1, 'Source NPI List is required.'),
  sourceNpiListName: z.string().optional(),
  countries: z.array(z.string()).min(1, 'At least one country is required.'),
  states: z.array(z.string()),
  lastSegmentedAt: z.number().optional(),
  reservationID: z.string().optional(),
});

export const listColumnSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    checked: z.boolean(),
    copied: z.boolean().optional(),
    type: z.enum(['Standard', 'Custom']),
    value: z.string(),
    dataType: z.enum(['str', 'int']),
    operators: z.array(z.string()),
  })
  .refine(
    (data) => {
      // If checked is true, value must not be empty
      if (data.checked && (!data.value || data.value.trim() === '')) {
        return false;
      }
      return true;
    },
    {
      message: 'An attribute is required for this column.',
      path: ['value'], // Point the error to the value field
    },
  );

export type ListColumn = z.infer<typeof listColumnSchema>;
export type IMedscapeMetadata = z.infer<typeof medscapeMetadataSchema>;
export type IConsentMedscapeMetadata = z.infer<typeof consentMedscapeMetadataSchema>;
export type IWorkspaceDetails = z.infer<typeof workspaceDetailsSchema>;

export interface MedscapePublishedContent {
  lastUpdatedAt: number;
  lastUpdatedBy: string;
}
